import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "../../ui/card";
import type { ProDraftCampaignRender } from "../../../lib/pro-agent-types";
import {
  MERGE_TOKEN_CHIP_LABELS,
  splitMergeTokens,
} from "../../../lib/merge-preview";

// draft_campaign confirm card (scenario 683, plan 188 task 803). draft_campaign
// already persisted a real `status=draft` campaign row (D1 — a draft sends
// nothing); this card never sends. "Review & send in Campaigns" hands off to
// the existing Campaigns list/composer, where the draft is already listed —
// nothing changes hands here.
export type CampaignConfirmCardProps = {
  draft: ProDraftCampaignRender;
};

export function CampaignConfirmCard({ draft }: CampaignConfirmCardProps) {
  const navigate = useNavigate();

  return (
    <Card data-testid="campaign-confirm-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-fg-muted">
            Draft
          </span>
          <p className="truncate text-sm font-medium text-fg">{draft.name}</p>
        </div>
        <p className="text-xs text-fg-muted">
          {draft.audience_label} · {draft.recipient_count} recipient
          {draft.recipient_count === 1 ? "" : "s"}
        </p>
      </CardHeader>
      <CardContent className="space-y-1.5">
        <p className="text-sm font-medium text-fg">{draft.subject}</p>
        <p
          data-testid="campaign-body-preview"
          className="whitespace-pre-wrap text-sm text-fg-muted"
        >
          {splitMergeTokens(draft.body_preview).map((segment, i) =>
            segment.isToken ? (
              <span
                key={i}
                data-testid="campaign-merge-chip"
                title={segment.text}
                className="inline-flex items-center rounded-md bg-pro-soft px-1.5 py-px text-[11px] font-semibold text-pro-ink"
              >
                {MERGE_TOKEN_CHIP_LABELS[segment.text] ?? segment.text}
              </span>
            ) : (
              <span key={i}>{segment.text}</span>
            ),
          )}
        </p>
      </CardContent>
      <CardFooter className="mt-1 flex-col items-stretch gap-2.5 rounded-b-lg border-t border-border-soft bg-muted/40 p-4">
        <Button size="sm" onClick={() => navigate("/campaigns")}>
          Review &amp; send in Campaigns
          <ArrowRight size={14} aria-hidden />
        </Button>
        <p className="text-[11px] text-fg-muted">
          Nothing sends until you review it in Campaigns.
        </p>
      </CardFooter>
    </Card>
  );
}
